import React, { useEffect, useState } from 'react';
import { Popconfirm, Flex, Form, Drawer } from 'antd';
import Table, { MAX_PAGE_SIZE } from 'components/Common/Table';
import { UserAddOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import * as actions from "store/actions/AuthActions"
import { notify } from "components";
import ProductForm from './ProductForm';
import FilterBar from 'components/Common/FilterBar';
import useUrlFilters from 'hooks/useUrlFilters';

// Search and the page live in the URL so the view can be shared as a link.
const URL_FILTERS = {
  search: { type: 'string' },
  page: { type: 'number', default: 1 },
  pageSize: { type: 'number', key: 'page_size', default: 50 },
};

const ProductsTable = () => {

  const [filters, setFilters] = useUrlFilters(URL_FILTERS);
  const urlSetter = (name) => (value) => setFilters({ [name]: value });

  const searchTerm = filters.search || "";
  const setSearchTerm = urlSetter('search');
  const [searchTermLive, setSearchTermLive] = useState(searchTerm);
  // Keep the box in step when the URL changes underneath it (back/forward).
  useEffect(() => setSearchTermLive(searchTerm), [searchTerm]);

  // Frontend pagination state
  const currentPage = filters.page;
  const setCurrentPage = urlSetter('page');
  const pageSize = Math.min(filters.pageSize, MAX_PAGE_SIZE);
  const setPageSize = urlSetter('pageSize');
  const [totalRows, setTotalRows] = useState(0);

  const [allProducts, setAllProducts] = useState([]);
  const [dataSource, setDataSource] = useState([]);
  const [loading, setLoading] = useState(false);

  const [openAddDrawer, setOpenAddDrawer] = useState(false);
  const [currentInstance, setCurrentInstance] = useState(null);
  const [taxRates, setTaxRates] = useState([]);
  const [form] = Form.useForm();

  const fetchAllProducts = async () => {
    setLoading(true);
    try {
      const response = await actions.loadAllProducts(searchTerm);
      if (response.status === 200) {
        const data = response.data;
        setAllProducts(data.results || []);
        setTotalRows(data.count || data.results?.length || 0);
      }
    } catch (e) {
      console.error('Error fetching products:', e);
    }
    setLoading(false);
  };

  // Frontend pagination logic
  const paginateData = () => {
    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    const paginatedData = allProducts.slice(startIndex, endIndex);
    setDataSource(paginatedData);
  };

  const onSearch = (value) => {
    if (searchTerm === value) {
      return;
    } else {
      setSearchTerm(value);
      setCurrentPage(1); // Reset to first page on search
    }
  };


  const fetchTaxRates = async () => {
    const response = await actions.loadTaxRates();
    setTaxRates(Array.isArray(response) ? response : response?.results || []);
  };

  useEffect(() => {
    fetchAllProducts();
  }, [searchTerm]);

  useEffect(() => {
    fetchTaxRates();
  }, []);

  // Apply frontend pagination when data or page changes
  useEffect(() => {
    paginateData();
  }, [allProducts, currentPage, pageSize]);

  const handleDelete = async (id) => {
    const newData = dataSource.find((item) => item.id === id);
    const deleted = await actions.deleteProducts(newData.id);
    if (deleted === true) {
      notify("success", "Product deleted successfully");
      // Refresh the data after deletion
      fetchAllProducts();
    } else {
      notify("error", "Failed to delete product");
    }
  };

  const rowSelection = {
    onChange: (selectedRowKeys, selectedRows) => {
      console.log(`selectedRowKeys: ${selectedRowKeys}`, 'selectedRows: ', selectedRows);
    },
  }


  // Descriptions are rich text; show them as plain text in the table.
  const toPlainText = (html) =>
    (html || '').replace(/<[^>]*>/g, ' ').replace(/&nbsp;/g, ' ').replace(/\s+/g, ' ').trim();

  const taxRateLabel = (taxRateId) => {
    const taxRate = taxRates.find((item) => item.id === taxRateId);
    return taxRate ? `${taxRate.title} ${taxRate.rate}%` : null;
  };

  const defaultColumns = [
    {
      title: 'TITLE',
      dataIndex: 'title',
      key: 'title',
      sorter: (a, b) => (a?.title || '').localeCompare(b?.title || ''),
    },
    {
      title: 'DESCRIPTION',
      dataIndex: 'description',
      key: 'description',
      ellipsis: true,
      render: (_, record) => toPlainText(record?.description || record?.notes),
      sorter: (a, b) =>
        toPlainText(a?.description || a?.notes).localeCompare(toPlainText(b?.description || b?.notes)),
    },
    {
      title: 'HSN/SAC',
      dataIndex: 'hsn',
      key: 'hsn',
      sorter: (a, b) => (a?.hsn || '').localeCompare(b?.hsn || ''),
    },
    {
      title: 'UNIT',
      dataIndex: 'unit',
      key: 'unit',
      sorter: (a, b) => (a?.unit || '').localeCompare(b?.unit || ''),
    },
    {
      title: 'QTY',
      dataIndex: 'quantity',
      key: 'quantity',
      align: 'right',
      sorter: (a, b) => (a?.quantity || 0) - (b?.quantity || 0),
    },
    {
      title: 'PRICE',
      dataIndex: 'price',
      key: 'price',
      sorter: (a, b) => a?.price - b?.price,
      align: 'right',
    },
    {
      title: 'TAX RATES',
      key: 'tax_rates',
      render: (_, record) =>
        [record?.tax_rate1, record?.tax_rate2, record?.tax_rate3]
          .map(taxRateLabel)
          .filter(Boolean)
          .join(', '),
    },
    {
      title: 'LINE TOTAL',
      dataIndex: 'line_total',
      key: 'line_total',
      align: 'right',
      sorter: (a, b) => (a?.line_total || 0) - (b?.line_total || 0),
    },
    {
      title: 'ACTIONS',
      dataIndex: 'Actions',
      key: 'Actions',
      render: (_, record) => {
        // console.log(record);
        return dataSource?.length >= 1 ? (
          <Flex gap="middle">
            <a>
              <EditOutlined
                onClick={() => {
                  setOpenAddDrawer(true)
                  setCurrentInstance(record);
                }}
              />
            </a>
            <Popconfirm title="Sure to delete?"
              onConfirm={() => handleDelete(record.id)}
            >
              <a>
                <DeleteOutlined />
              </a>
            </Popconfirm>
          </Flex>
        ) : null
      },
    },
  ];

  const handleSave = (row) => {
    const newData = [...dataSource];
    const index = newData.findIndex((item) => row.key === item.key);
    const item = newData[index];
    newData.splice(index, 1, {
      ...item,
      ...row,
    });
    setDataSource(newData);
  };

  const columns = defaultColumns.map((col) => {
    if (!col.editable) {
      return col;
    }
    return {
      ...col,
      onCell: (record) => ({
        record,
        editable: col.editable,
        dataIndex: col.dataIndex,
        title: col.title,
        handleSave,
      }),
    };
  });

  const [selectionType] = useState('checkbox');

  return (
    <div style={{ padding: 10 }}>
      <Flex gap='large' justify='space-between'>
        <Flex gap='large' align='center' wrap>
          <div>
            Showing {((currentPage - 1) * pageSize) + 1}-{Math.min(currentPage * pageSize, totalRows)} of {totalRows} products
          </div>

          <FilterBar
            searchValue={searchTermLive}
            onSearchChange={setSearchTermLive}
            onSearch={onSearch}
            searchPlaceholder="Search products"
            onRefresh={() => fetchAllProducts()}
            refreshing={loading}
            addLabel="Add Product"
            addIcon={<UserAddOutlined />}
            onAdd={() => {
              setCurrentInstance(null);
              form.resetFields();
              setOpenAddDrawer(true);
            }}
          />
        </Flex>
      </Flex>
      <div style={{ marginTop: 20 }}>
        <Table
          rowSelection={{
            type: selectionType,
            ...rowSelection,
          }}
          loading={loading}
          bordered
          size="small"
          dataSource={dataSource}
          columns={columns}
          rowKey={row => row.id}
          pagination={{
            current: currentPage,
            pageSize: pageSize,
            total: totalRows,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
            pageSizeOptions: ['10', '25', '50', '100'],
            onChange: (page, size) => {
              setCurrentPage(page);
              if (size !== pageSize) {
                setPageSize(size);
                setCurrentPage(1);
              }
            },
          }}
          scroll={{ y: `calc(100vh - 350px)` }}
        />
      </div>
      <Drawer
        title={`${currentInstance ? 'Edit' : 'Add'} Product`}
        placement="right"
        width={"35%"}
        onClose={() => {
          setCurrentInstance(null)
          setOpenAddDrawer(false)
          form.resetFields();
        }}
        open={openAddDrawer}
        style={{ zIndex: 10 }}
      >
        <ProductForm
          instance={currentInstance}
          form={form}
          taxRates={taxRates}
          reload={() => {
            setCurrentInstance(null);
            setOpenAddDrawer(false);
            form.resetFields();
            fetchAllProducts();
          }}
        />
      </Drawer>
    </div>
  );
};
export default ProductsTable;